import { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Plus, Search, Phone, MapPin, FileText, DollarSign, History,
    Trash2, Edit2, Camera, Upload, Eye, Download, X, Receipt, User, MessageCircle
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { useAuth } from '../context/AuthContext';
import { patchReadCache, isOffline } from '../lib/axiosOffline';
import { toast } from 'sonner';
import { PhoneInput } from '../components/PhoneInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convierte "YYYY-MM-DD" (o ISO) a "DD/MM/AAAA". Si no parsea, devuelve el original.
const formatFechaDMA = (fecha) => {
    if (!fecha || typeof fecha !== 'string') return fecha || '';
    const m = fecha.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[3]}/${m[2]}/${m[1]}`;
    return fecha;
};

const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : (Date.now() + '-' + Math.random().toString(36).slice(2));

const initialClienteForm = {
    nombre: '',
    telefono: '',
    direccion: '',
    rfc: '',
    notas: ''
};

const initialCreditoForm = {
    fecha: new Date().toISOString().split('T')[0],
    concepto: '',
    monto: 0,
    observaciones: ''
};

const initialAbonoForm = {
    fecha: new Date().toISOString().split('T')[0],
    monto: 0,
    forma_pago: 'efectivo',
    observaciones: ''
};

export const Clientes = () => {
    const { isAdmin } = useAuth();
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [clienteModalOpen, setClienteModalOpen] = useState(false);
    const [detalleModalOpen, setDetalleModalOpen] = useState(false);
    const [creditoModalOpen, setCreditoModalOpen] = useState(false);
    const [abonoModalOpen, setAbonoModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [clienteForm, setClienteForm] = useState(initialClienteForm);
    const [fotoFile, setFotoFile] = useState(null);
    const [fotoPreview, setFotoPreview] = useState(null);
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [detalleTab, setDetalleTab] = useState('libreta'); // 'libreta' | 'notas'
    const [detalle, setDetalle] = useState(null);
    const [creditoForm, setCreditoForm] = useState(initialCreditoForm);
    const [abonoForm, setAbonoForm] = useState(initialAbonoForm);
    const [comprobanteFile, setComprobanteFile] = useState(null);
    const [selectedCredito, setSelectedCredito] = useState(null);
    // Abono global (saldo total)
    const [abonoGlobalModal, setAbonoGlobalModal] = useState(null);  // cliente al que se le hace abono global
    const [abonoGlobalForm, setAbonoGlobalForm] = useState(initialAbonoForm);
    const [comprobanteGlobalFile, setComprobanteGlobalFile] = useState(null);
    // Edicion de abono existente
    const [editAbonoModal, setEditAbonoModal] = useState(null); // {id, monto, fecha, forma_pago, observaciones}

    useEffect(() => { fetchClientes(); }, []);

    const fetchClientes = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/clientes`);
            setClientes(res.data);
        } catch (e) {
            toast.error('Error al cargar clientes');
        } finally {
            setLoading(false);
        }
    };

    const fetchDetalle = async (cliente_id) => {
        try {
            const res = await axios.get(`${API_URL}/api/clientes/${cliente_id}`);
            setDetalle(res.data);
        } catch (e) {
            // Fallback offline: construir desde state local
            const cli = clientes.find(c => c.id === cliente_id);
            if (cli) {
                setDetalle({
                    ...cli,
                    creditos: [],
                    abonos: [],
                    total_facturado: cli.total_facturado || 0,
                    total_abonado: cli.total_abonado || 0,
                    saldo_pendiente: cli.saldo_pendiente || 0
                });
                toast.warning('Mostrando datos limitados (sin conexion)');
            } else {
                toast.error('Datos del cliente no disponibles offline');
            }
        }
    };

    const handleClienteSubmit = async () => {
        if (!clienteForm.nombre.trim()) { toast.error('Nombre requerido'); return; }
        const offline = isOffline();
        try {
            let cliId;
            if (editingId) {
                await axios.put(`${API_URL}/api/clientes/${editingId}`, clienteForm);
                cliId = editingId;
                toast.success(offline ? 'Edicion guardada (se sincronizara)' : 'Cliente actualizado');
            } else {
                cliId = newId();
                const payload = { ...clienteForm, id: cliId };
                const res = await axios.post(`${API_URL}/api/clientes`, payload);
                if (res?.data?._offline) {
                    // Optimistic local insert para que aparezca en la lista y POS lo vea
                    const nuevo = {
                        ...payload,
                        estatus: 'activo',
                        foto_url: '',
                        total_facturado: 0,
                        total_abonado: 0,
                        saldo_pendiente: 0,
                        num_creditos: 0,
                        num_creditos_pendientes: 0,
                        created_at: new Date().toISOString()
                    };
                    setClientes(prev => [nuevo, ...prev]);
                    patchReadCache('/api/clientes', (curr) => Array.isArray(curr) ? [nuevo, ...curr] : [nuevo]);
                    toast.success('Cliente creado offline (se sincronizara)');
                } else {
                    cliId = res.data.id;
                    toast.success('Cliente creado');
                }
            }
            // Foto upload solo si hay conexion (multipart no se puede encolar)
            if (fotoFile && cliId && !offline) {
                const fd = new FormData();
                fd.append('file', fotoFile);
                try {
                    await axios.post(`${API_URL}/api/clientes/${cliId}/foto`, fd, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                } catch (e) { /* la foto puede esperar */ }
            } else if (fotoFile && offline) {
                toast.warning('La foto se subira cuando vuelva la conexion');
            }
            resetClienteForm();
            setClienteModalOpen(false);
            if (!offline) fetchClientes();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error');
        }
    };

    const handleEdit = (cli) => {
        setEditingId(cli.id);
        setClienteForm({
            nombre: cli.nombre || '',
            telefono: cli.telefono || '',
            direccion: cli.direccion || '',
            rfc: cli.rfc || '',
            notas: cli.notas || ''
        });
        setFotoPreview(cli.foto_url ? `${API_URL}${cli.foto_url}` : null);
        setFotoFile(null);
        setClienteModalOpen(true);
    };

    const handleDelete = async (cli) => {
        if (!window.confirm(`¿Eliminar al cliente "${cli.nombre}"?`)) return;
        const offline = isOffline();
        try {
            await axios.delete(`${API_URL}/api/clientes/${cli.id}`);
            // Optimistic remove local (siempre, online u offline)
            setClientes(prev => prev.filter(c => c.id !== cli.id));
            patchReadCache('/api/clientes', (curr) => Array.isArray(curr) ? curr.filter(c => c.id !== cli.id) : curr);
            toast.success(offline ? 'Cliente eliminado offline (se sincronizara)' : 'Cliente eliminado');
            if (!offline) fetchClientes();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al eliminar');
        }
    };

    const resetClienteForm = () => {
        setClienteForm(initialClienteForm);
        setEditingId(null);
        setFotoFile(null);
        setFotoPreview(null);
    };

    const handleFotoChange = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFotoFile(f);
        const reader = new FileReader();
        reader.onload = (ev) => setFotoPreview(ev.target.result);
        reader.readAsDataURL(f);
    };

    const openDetalle = async (cli) => {
        setSelectedCliente(cli);
        setDetalle(null);
        setDetalleModalOpen(true);
        await fetchDetalle(cli.id);
    };

    const handleCreditoSubmit = async () => {
        if (!creditoForm.concepto.trim()) { toast.error('Concepto requerido'); return; }
        if (!creditoForm.monto || creditoForm.monto <= 0) { toast.error('Monto invalido'); return; }
        const offline = isOffline();
        try {
            const cred_id = newId();
            const payload = { ...creditoForm, id: cred_id };
            const res = await axios.post(`${API_URL}/api/clientes/${selectedCliente.id}/creditos`, payload);
            if (res?.data?._offline) {
                const monto = parseFloat(creditoForm.monto) || 0;
                const nuevo = {
                    ...payload,
                    cliente_id: selectedCliente.id,
                    estatus: 'pendiente',
                    abonado: 0,
                    saldo: monto,
                    num_abonos: 0,
                    created_at: new Date().toISOString()
                };
                setDetalle(d => d ? {
                    ...d,
                    creditos: [nuevo, ...d.creditos],
                    total_facturado: d.total_facturado + nuevo.monto,
                    saldo_pendiente: d.saldo_pendiente + nuevo.monto
                } : d);
                setClientes(prev => prev.map(c => c.id === selectedCliente.id ? {
                    ...c,
                    total_facturado: (c.total_facturado || 0) + nuevo.monto,
                    saldo_pendiente: (c.saldo_pendiente || 0) + nuevo.monto,
                    num_creditos: (c.num_creditos || 0) + 1,
                    num_creditos_pendientes: (c.num_creditos_pendientes || 0) + 1
                } : c));
                // Persistir en cache local para que sobreviva al cerrar/reabrir el modal
                patchReadCache(`/api/clientes/${selectedCliente.id}`, (curr) => curr ? {
                    ...curr,
                    creditos: [nuevo, ...(curr.creditos || [])],
                    total_facturado: (curr.total_facturado || 0) + nuevo.monto,
                    saldo_pendiente: (curr.saldo_pendiente || 0) + nuevo.monto
                } : curr);
                patchReadCache('/api/clientes', (curr) => Array.isArray(curr) ? curr.map(c => c.id === selectedCliente.id ? {
                    ...c,
                    total_facturado: (c.total_facturado || 0) + nuevo.monto,
                    saldo_pendiente: (c.saldo_pendiente || 0) + nuevo.monto,
                    num_creditos: (c.num_creditos || 0) + 1,
                    num_creditos_pendientes: (c.num_creditos_pendientes || 0) + 1
                } : c) : curr);
                toast.success('Cuenta a credito guardada offline (se sincronizara)');
            } else {
                toast.success('Cuenta a credito registrada');
            }
            setCreditoForm(initialCreditoForm);
            setCreditoModalOpen(false);
            if (!offline) {
                await fetchDetalle(selectedCliente.id);
                fetchClientes();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error');
        }
    };

    const handleAbonoGlobalSubmit = async () => {
        if (!abonoGlobalModal) return;
        const monto = parseFloat(abonoGlobalForm.monto) || 0;
        if (monto <= 0) { toast.error('Monto invalido'); return; }
        if (monto > (abonoGlobalModal.saldo_pendiente || 0) + 0.01) {
            toast.error(`El abono excede el saldo total pendiente ($${(abonoGlobalModal.saldo_pendiente || 0).toFixed(2)})`);
            return;
        }
        try {
            const res = await axios.post(
                `${API_URL}/api/clientes/${abonoGlobalModal.id}/abono-global`,
                abonoGlobalForm
            );
            // Subir comprobante a TODAS las cuentas afectadas
            if (comprobanteGlobalFile && res.data?.ids?.length) {
                const fd = new FormData();
                fd.append('file', comprobanteGlobalFile);
                for (const ab_id of res.data.ids) {
                    try {
                        await axios.post(`${API_URL}/api/abonos/${ab_id}/comprobante`, fd, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                        });
                    } catch (err) { /* no crash */ }
                }
            }
            toast.success(`Abono $${monto.toFixed(2)} aplicado a ${res.data.abonos_creados} cuenta${res.data.abonos_creados !== 1 ? 's' : ''}`);
            setAbonoGlobalModal(null);
            setAbonoGlobalForm(initialAbonoForm);
            setComprobanteGlobalFile(null);
            fetchClientes();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al abonar');
        }
    };

    const openAbonoGlobal = (cli) => {
        setAbonoGlobalModal(cli);
        setAbonoGlobalForm({ ...initialAbonoForm, monto: '' });
        setComprobanteGlobalFile(null);
    };

    const handleAbonoSubmit = async () => {
        if (!selectedCredito) return;
        if (!abonoForm.monto || abonoForm.monto <= 0) { toast.error('Monto invalido'); return; }
        if (abonoForm.monto > selectedCredito.saldo + 0.01) {
            toast.error(`El abono excede el saldo pendiente ($${selectedCredito.saldo.toFixed(2)})`);
            return;
        }
        const offline = isOffline();
        try {
            const ab_id = newId();
            const payload = { ...abonoForm, id: ab_id };
            const res = await axios.post(
                `${API_URL}/api/creditos/${selectedCredito.id}/abonos`,
                payload
            );
            const offlineRes = res?.data?._offline;
            const abonoIdRemoto = offlineRes ? ab_id : (res.data.id || ab_id);
            if (comprobanteFile && abonoIdRemoto && !offline) {
                const fd = new FormData();
                fd.append('file', comprobanteFile);
                try {
                    await axios.post(`${API_URL}/api/abonos/${abonoIdRemoto}/comprobante`, fd, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                } catch (e) { /* el comprobante puede esperar */ }
            } else if (comprobanteFile && offline) {
                toast.warning('El comprobante se subira cuando vuelva la conexion');
            }
            if (offlineRes) {
                const nuevoAb = {
                    ...payload,
                    cliente_id: selectedCliente.id,
                    credito_id: selectedCredito.id,
                    monto: parseFloat(payload.monto) || 0,
                    comprobante_url: '',
                    created_at: new Date().toISOString()
                };
                const updateCreditos = (creditos) => (creditos || []).map(cr => cr.id === selectedCredito.id ? {
                    ...cr,
                    abonado: (cr.abonado || 0) + nuevoAb.monto,
                    saldo: Math.max(0, (cr.saldo || 0) - nuevoAb.monto),
                    num_abonos: (cr.num_abonos || 0) + 1,
                    estatus: ((cr.saldo || 0) - nuevoAb.monto) <= 0.01 ? 'saldada' : 'pendiente'
                } : cr);
                setDetalle(d => d ? {
                    ...d,
                    abonos: [nuevoAb, ...d.abonos],
                    creditos: updateCreditos(d.creditos),
                    total_abonado: d.total_abonado + nuevoAb.monto,
                    saldo_pendiente: Math.max(0, d.saldo_pendiente - nuevoAb.monto)
                } : d);
                setClientes(prev => prev.map(c => c.id === selectedCliente.id ? {
                    ...c,
                    total_abonado: (c.total_abonado || 0) + nuevoAb.monto,
                    saldo_pendiente: Math.max(0, (c.saldo_pendiente || 0) - nuevoAb.monto)
                } : c));
                // Persistir en cache local para sobrevivir al cerrar/reabrir el modal
                patchReadCache(`/api/clientes/${selectedCliente.id}`, (curr) => curr ? {
                    ...curr,
                    abonos: [nuevoAb, ...(curr.abonos || [])],
                    creditos: updateCreditos(curr.creditos),
                    total_abonado: (curr.total_abonado || 0) + nuevoAb.monto,
                    saldo_pendiente: Math.max(0, (curr.saldo_pendiente || 0) - nuevoAb.monto)
                } : curr);
                patchReadCache('/api/clientes', (curr) => Array.isArray(curr) ? curr.map(c => c.id === selectedCliente.id ? {
                    ...c,
                    total_abonado: (c.total_abonado || 0) + nuevoAb.monto,
                    saldo_pendiente: Math.max(0, (c.saldo_pendiente || 0) - nuevoAb.monto)
                } : c) : curr);
                toast.success('Abono guardado offline (se sincronizara)');
            } else {
                toast.success('Abono registrado');
            }
            setAbonoForm(initialAbonoForm);
            setComprobanteFile(null);
            setAbonoModalOpen(false);
            setSelectedCredito(null);
            if (!offline) {
                await fetchDetalle(selectedCliente.id);
                fetchClientes();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error');
        }
    };

    const openAbono = (credito) => {
        setSelectedCredito(credito);
        setAbonoForm({ ...initialAbonoForm, monto: credito.saldo });
        setComprobanteFile(null);
        setAbonoModalOpen(true);
    };

    const handleDeleteCredito = async (credito) => {
        if (!window.confirm('¿Eliminar esta cuenta a credito? (no debe tener abonos)')) return;
        try {
            await axios.delete(`${API_URL}/api/creditos/${credito.id}`);
            toast.success('Cuenta eliminada');
            await fetchDetalle(selectedCliente.id);
            fetchClientes();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error');
        }
    };

    const handleDeleteAbono = async (abono) => {
        if (!window.confirm('¿Eliminar este abono?')) return;
        try {
            await axios.delete(`${API_URL}/api/abonos/${abono.id}`);
            toast.success('Abono eliminado');
            await fetchDetalle(selectedCliente.id);
            fetchClientes();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error');
        }
    };

    const handleSubirComprobanteAbono = async (abono) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*,application/pdf';
        input.onchange = async () => {
            const file = input.files?.[0];
            if (!file) return;
            try {
                const fd = new FormData();
                fd.append('file', file);
                await axios.post(`${API_URL}/api/abonos/${abono.id}/comprobante`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Comprobante subido');
                await fetchDetalle(selectedCliente.id);
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al subir');
            }
        };
        input.click();
    };

    const handleEditAbono = (abono) => {
        setEditAbonoModal({
            id: abono.id,
            monto: abono.monto || 0,
            fecha: (abono.fecha || '').slice(0, 10),
            forma_pago: abono.forma_pago || 'efectivo',
            observaciones: abono.observaciones || ''
        });
    };

    const handleSaveEditAbono = async () => {
        if (!editAbonoModal) return;
        const monto = parseFloat(editAbonoModal.monto);
        if (!monto || monto <= 0) { toast.error('Monto invalido'); return; }
        try {
            await axios.patch(`${API_URL}/api/abonos/${editAbonoModal.id}`, {
                monto,
                fecha: editAbonoModal.fecha,
                forma_pago: editAbonoModal.forma_pago,
                observaciones: editAbonoModal.observaciones
            });
            toast.success('Abono corregido');
            setEditAbonoModal(null);
            await fetchDetalle(selectedCliente.id);
            fetchClientes();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al guardar');
        }
    };

    const downloadEstadoCuenta = async (cli) => {
        try {
            const res = await axios.get(
                `${API_URL}/api/clientes/${cli.id}/estado-cuenta-pdf`,
                { responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `estado_cuenta_${(cli.nombre || 'cliente').replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast.error('Error al generar PDF');
        }
    };

    // Normaliza un telefono mexicano (o internacional) para wa.me:
    //  - Quita espacios, guiones, parentesis, +.
    //  - Si tiene 10 digitos (sin lada) → asume +52.
    //  - Si ya empieza con 52 / 1 / 34 / etc → respeta esa lada.
    //  - Devuelve solo digitos.
    const normalizarTelefonoWA = (telefono) => {
        const digits = (telefono || '').replace(/[^\d]/g, '');
        if (!digits) return '';
        if (digits.length === 10) return `52${digits}`;  // Mexico default
        if (digits.length === 11 && digits.startsWith('1')) return digits;  // EEUU 1XXXXXXXXXX
        if (digits.length === 12 && digits.startsWith('52')) return digits; // Mexico ya con lada
        // Numeros sin prefijo de pais detectable: asumir Mexico
        if (digits.length < 10) return `52${digits}`;
        return digits;
    };

    const enviarPorWhatsApp = (cli) => {
        const numero = normalizarTelefonoWA(cli.telefono);
        if (!numero) {
            toast.error(`${cli.nombre} no tiene telefono registrado`);
            return;
        }
        const saldo = (cli.saldo_pendiente || 0).toFixed(2);
        const facturado = (cli.total_facturado || 0).toFixed(2);
        const abonado = (cli.total_abonado || 0).toFixed(2);
        const link_pdf = `${API_URL}/api/clientes/${cli.id}/estado-cuenta-pdf`;
        const saludo = `Hola ${(cli.nombre || '').split(' ')[0] || ''}, te comparto tu estado de cuenta de Karry Jean's:`;
        const cuerpo = (
            `${saludo}\n\n` +
            `📊 *Resumen*\n` +
            `• Total facturado: $${facturado}\n` +
            `• Total abonado: $${abonado}\n` +
            `• Saldo pendiente: *$${saldo}*\n\n` +
            `📄 PDF completo: ${link_pdf}\n\n` +
            `Cualquier duda con gusto te apoyo. ¡Gracias!`
        );
        const url = `https://wa.me/${numero}?text=${encodeURIComponent(cuerpo)}`;
        window.open(url, '_blank');
    };

    const filtered = clientes.filter(c =>
        (c.nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.telefono || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalDeuda = clientes.reduce((s, c) => s + (c.saldo_pendiente || 0), 0);
    const totalFacturado = clientes.reduce((s, c) => s + (c.total_facturado || 0), 0);
    const clientesConDeuda = clientes.filter(c => (c.saldo_pendiente || 0) > 0.01).length;

    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div></div>;

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="clientes-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100">Clientes</h1>
                    <p className="text-zinc-400 mt-1">Cuentas a credito, abonos y estado de cuenta</p>
                </div>
                <Button
                    onClick={() => { resetClienteForm(); setClienteModalOpen(true); }}
                    className="bg-amber-400 hover:bg-amber-500 text-zinc-900 font-bold"
                    data-testid="btn-nuevo-cliente"
                >
                    <Plus className="w-4 h-4 mr-2" /> Nuevo Cliente
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-400/10"><User className="w-5 h-5 text-blue-400" /></div>
                        <div>
                            <p className="text-xs text-zinc-400">Total Clientes</p>
                            <p className="text-xl font-bold text-zinc-100">{clientes.length}</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-amber-400/10"><DollarSign className="w-5 h-5 text-amber-400" /></div>
                        <div>
                            <p className="text-xs text-zinc-400">Deuda Total</p>
                            <p className="text-xl font-bold text-amber-400">${totalDeuda.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-emerald-400/10"><Receipt className="w-5 h-5 text-emerald-400" /></div>
                        <div>
                            <p className="text-xs text-zinc-400">Con saldo pendiente</p>
                            <p className="text-xl font-bold text-emerald-400">{clientesConDeuda}</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                    placeholder="Buscar por nombre o telefono..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                    data-testid="search-clientes"
                />
            </div>

            {/* Lista */}
            <Card className="card">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-zinc-800">
                                    <TableHead className="table-header">Cliente</TableHead>
                                    <TableHead className="table-header">Telefono</TableHead>
                                    <TableHead className="table-header">Cuentas</TableHead>
                                    <TableHead className="table-header">Facturado</TableHead>
                                    <TableHead className="table-header">Abonado</TableHead>
                                    <TableHead className="table-header">Saldo</TableHead>
                                    <TableHead className="table-header">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map((c) => (
                                    <TableRow key={c.id} className="table-row" data-testid={`cliente-row-${c.id}`}>
                                        <TableCell className="table-cell">
                                            <div className="flex items-center gap-3">
                                                {c.foto_url ? (
                                                    <img src={`${API_URL}${c.foto_url}`} alt={c.nombre} className="w-9 h-9 rounded-full object-cover border border-zinc-700" />
                                                ) : (
                                                    <div className="w-9 h-9 rounded-full bg-zinc-700 flex items-center justify-center"><User className="w-4 h-4 text-zinc-400" /></div>
                                                )}
                                                <div>
                                                    <p className="font-medium text-zinc-100">{c.nombre}</p>
                                                    {c.rfc && <p className="text-[10px] text-zinc-500">{c.rfc}</p>}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="table-cell text-zinc-300">{c.telefono || '-'}</TableCell>
                                        <TableCell className="table-cell">
                                            {c.num_creditos_pendientes > 0 ? (
                                                <span className="badge-warning">{c.num_creditos_pendientes} pend. / {c.num_creditos}</span>
                                            ) : (
                                                <span className="badge-success">{c.num_creditos} cuentas</span>
                                            )}
                                        </TableCell>
                                        <TableCell className="table-cell text-zinc-300">${(c.total_facturado || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</TableCell>
                                        <TableCell className="table-cell text-emerald-400">${(c.total_abonado || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</TableCell>
                                        <TableCell className="table-cell">
                                            <span className={`font-bold ${(c.saldo_pendiente || 0) > 0.01 ? 'text-amber-400' : 'text-zinc-500'}`}>
                                                ${(c.saldo_pendiente || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                            </span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <div className="flex items-center gap-1">
                                                {(c.saldo_pendiente || 0) > 0.01 && (
                                                    <Button size="sm" onClick={() => openAbonoGlobal(c)} className="h-7 bg-emerald-500 hover:bg-emerald-600 text-xs px-2" data-testid={`btn-abonar-total-${c.id}`} title="Abonar al saldo total (FIFO)">
                                                        <DollarSign className="w-3.5 h-3.5 mr-0.5" /> Abonar
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="ghost" onClick={() => openDetalle(c)} data-testid={`btn-ver-${c.id}`} title="Ver detalle">
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                <Button size="sm" variant="ghost" onClick={() => downloadEstadoCuenta(c)} data-testid={`btn-pdf-${c.id}`} title="Estado de cuenta PDF">
                                                    <Download className="w-4 h-4" />
                                                </Button>
                                                {c.telefono && (
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        onClick={() => enviarPorWhatsApp(c)}
                                                        title="Enviar estado de cuenta por WhatsApp"
                                                        className="text-emerald-400 hover:text-emerald-300"
                                                        data-testid={`btn-wa-${c.id}`}
                                                    >
                                                        <MessageCircle className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="ghost" onClick={() => handleEdit(c)} title="Editar">
                                                    <Edit2 className="w-4 h-4" />
                                                </Button>
                                                {isAdmin && (
                                                    <Button size="sm" variant="ghost" onClick={() => handleDelete(c)} title="Eliminar" className="text-red-400 hover:text-red-300">
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {filtered.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-10 text-zinc-500">Sin clientes registrados</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Modal: Nuevo/Editar Cliente */}
            <Dialog open={clienteModalOpen} onOpenChange={setClienteModalOpen}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{editingId ? 'Editar' : 'Nuevo'} Cliente</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="flex flex-col items-center gap-2">
                            {fotoPreview ? (
                                <img src={fotoPreview} alt="preview" className="w-24 h-24 rounded-full object-cover border-2 border-amber-400" />
                            ) : (
                                <div className="w-24 h-24 rounded-full bg-zinc-800 flex items-center justify-center"><Camera className="w-8 h-8 text-zinc-500" /></div>
                            )}
                            <label className="cursor-pointer text-xs text-amber-400 hover:underline">
                                <Upload className="w-3 h-3 inline mr-1" />
                                Subir foto (opcional)
                                <input type="file" accept="image/*" onChange={handleFotoChange} className="hidden" data-testid="cliente-foto-input" />
                            </label>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div className="form-group">
                                <Label className="form-label">Nombre *</Label>
                                <Input value={clienteForm.nombre} onChange={(e) => setClienteForm({ ...clienteForm, nombre: e.target.value })} data-testid="input-cliente-nombre" />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Telefono</Label>
                                <PhoneInput
                                    value={clienteForm.telefono}
                                    onChange={(v) => setClienteForm({ ...clienteForm, telefono: v })}
                                    placeholder="5512345678"
                                    testId="cliente-tel"
                                />
                            </div>
                            <div className="form-group sm:col-span-2">
                                <Label className="form-label">Direccion</Label>
                                <Input value={clienteForm.direccion} onChange={(e) => setClienteForm({ ...clienteForm, direccion: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">RFC</Label>
                                <Input value={clienteForm.rfc} onChange={(e) => setClienteForm({ ...clienteForm, rfc: e.target.value })} />
                            </div>
                            <div className="form-group sm:col-span-2">
                                <Label className="form-label">Notas</Label>
                                <Textarea rows={2} value={clienteForm.notas} onChange={(e) => setClienteForm({ ...clienteForm, notas: e.target.value })} />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setClienteModalOpen(false)}>Cancelar</Button>
                        <Button onClick={handleClienteSubmit} className="bg-amber-400 hover:bg-amber-500 text-zinc-900 font-bold" data-testid="btn-guardar-cliente">
                            {editingId ? 'Actualizar' : 'Crear'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal: Detalle del Cliente */}
            <Dialog open={detalleModalOpen} onOpenChange={setDetalleModalOpen}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-5xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <User className="w-5 h-5 text-amber-400" />
                            {selectedCliente?.nombre}
                        </DialogTitle>
                    </DialogHeader>

                    {!detalle ? (
                        <div className="py-10 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-400 mx-auto"></div></div>
                    ) : (() => {
                        // Ordenamiento cronologico ASCENDENTE (vieja → reciente) para notas y abonos.
                        const sortAsc = (a, b) => (a.fecha || '').localeCompare(b.fecha || '') || (a.created_at || '').localeCompare(b.created_at || '');
                        const creditosOrdenados = [...(detalle.creditos || [])].sort(sortAsc);
                        const abonosOrdenados = [...(detalle.abonos || [])].sort(sortAsc);

                        // ===== Construir LIBRETA combinada =====
                        // Las notas entran como movimientos "+", los abonos como "-".
                        // Si varios abonos comparten pago_global_id (un mismo pago aplicado a varias notas)
                        // se fusionan en una sola linea con el total. Se asume la fecha/created_at del primero.
                        const movimientos = [];
                        let notaIdx = 0;
                        for (const cr of creditosOrdenados) {
                            notaIdx += 1;
                            movimientos.push({
                                tipo: 'nota',
                                id: cr.id,
                                fecha: cr.fecha,
                                created_at: cr.created_at || '',
                                etiqueta: `Nota ${notaIdx}`,
                                concepto: cr.concepto || '',
                                monto: cr.monto || 0,
                                signo: 1,
                                _credito: cr,
                            });
                        }
                        const vistos = new Set();
                        for (const ab of abonosOrdenados) {
                            const pg = ab.pago_global_id || '';
                            if (pg) {
                                if (vistos.has(pg)) continue;
                                vistos.add(pg);
                                const grupo = abonosOrdenados.filter(x => x.pago_global_id === pg);
                                const total = grupo.reduce((s, x) => s + (x.monto || 0), 0);
                                movimientos.push({
                                    tipo: 'abono',
                                    id: ab.id,
                                    fecha: ab.fecha,
                                    created_at: ab.created_at || '',
                                    etiqueta: 'Abono',
                                    concepto: (ab.forma_pago || '').toString(),
                                    monto: total,
                                    signo: -1,
                                    comprobante_url: ab.comprobante_url || '',
                                    _abono: ab,
                                    _grupo: grupo,
                                });
                            } else {
                                movimientos.push({
                                    tipo: 'abono',
                                    id: ab.id,
                                    fecha: ab.fecha,
                                    created_at: ab.created_at || '',
                                    etiqueta: 'Abono',
                                    concepto: (ab.forma_pago || '').toString(),
                                    monto: ab.monto || 0,
                                    signo: -1,
                                    comprobante_url: ab.comprobante_url || '',
                                    _abono: ab,
                                });
                            }
                        }
                        // Mezclar y ordenar cronologico ascendente
                        movimientos.sort(sortAsc);
                        // Calcular saldo acumulado tipo libreta
                        let acumulado = 0;
                        const libreta = movimientos.map((m, idx) => {
                            acumulado += m.signo * (m.monto || 0);
                            return { ...m, ordinal: idx + 1, saldo: acumulado };
                        });

                        return (
                        <div className="space-y-5">
                            {/* Acciones rapidas */}
                            <div className="flex flex-wrap gap-2">
                                <Button
                                    onClick={() => { setCreditoForm(initialCreditoForm); setCreditoModalOpen(true); }}
                                    className="bg-amber-400 hover:bg-amber-500 text-zinc-900 font-bold"
                                    data-testid="btn-nuevo-credito"
                                >
                                    <Plus className="w-4 h-4 mr-2" /> Nueva nota de credito
                                </Button>
                                <Button variant="outline" onClick={() => downloadEstadoCuenta(selectedCliente)}>
                                    <Download className="w-4 h-4 mr-2" /> Estado de cuenta PDF
                                </Button>
                                {selectedCliente.telefono && (
                                    <Button
                                        variant="outline"
                                        onClick={() => enviarPorWhatsApp(selectedCliente)}
                                        className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                                        data-testid="btn-wa-detalle"
                                    >
                                        <MessageCircle className="w-4 h-4 mr-2" /> Enviar por WhatsApp
                                    </Button>
                                )}
                            </div>

                            {/* Tabs centrados: Libreta | Notas */}
                            <div className="flex justify-center">
                                <div className="inline-flex bg-zinc-800 rounded-lg p-1 border border-zinc-700">
                                    <button
                                        type="button"
                                        onClick={() => setDetalleTab('libreta')}
                                        className={`px-6 py-1.5 rounded-md text-sm font-semibold transition ${detalleTab === 'libreta' ? 'bg-amber-400 text-zinc-900' : 'text-zinc-400 hover:text-zinc-200'}`}
                                        data-testid="tab-libreta"
                                    >
                                        Libreta ({libreta.length})
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setDetalleTab('notas')}
                                        className={`px-6 py-1.5 rounded-md text-sm font-semibold transition ${detalleTab === 'notas' ? 'bg-amber-400 text-zinc-900' : 'text-zinc-400 hover:text-zinc-200'}`}
                                        data-testid="tab-notas"
                                    >
                                        Notas ({creditosOrdenados.length})
                                    </button>
                                </div>
                            </div>

                            {detalleTab === 'libreta' && (
                                <div data-testid="seccion-libreta">
                                    <p className="text-xs text-zinc-500 mb-3 text-center italic">
                                        Cuenta corrida estilo libreta &mdash; cada movimiento debajo del anterior con el saldo recalculado.
                                    </p>
                                    <div className="rounded-lg border border-amber-400/30 bg-[#fdf6e3] p-4 sm:p-6 font-mono text-zinc-900 space-y-3 max-h-[60vh] overflow-y-auto"
                                         style={{ backgroundImage: 'repeating-linear-gradient(transparent, transparent 31px, #cfd8d4 31px, #cfd8d4 32px)' }}>
                                        {libreta.length === 0 && (
                                            <p className="text-center text-zinc-500 italic py-8">Sin movimientos registrados</p>
                                        )}
                                        {libreta.map((m) => {
                                            const isNota = m.tipo === 'nota';
                                            const colorAmt = isNota ? 'text-emerald-800' : 'text-red-700';
                                            const signo = isNota ? '+' : '-';
                                            return (
                                                <div
                                                    key={`${m.tipo}-${m.id}`}
                                                    className="border-b border-zinc-400/60 pb-2"
                                                    data-testid={`libreta-row-${m.tipo}-${m.id}`}
                                                >
                                                    <div className="flex items-baseline justify-between gap-2">
                                                        <div className="flex items-baseline gap-2 min-w-0 flex-1">
                                                            <span className={`font-bold text-base sm:text-lg ${colorAmt}`}>
                                                                {isNota ? `${m.etiqueta}:` : `${m.etiqueta}:`}
                                                            </span>
                                                            <span className={`font-extrabold text-lg sm:text-xl ${colorAmt}`}>
                                                                {signo}${m.monto.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                                            </span>
                                                            {!isNota && (
                                                                <span className="text-xs text-zinc-600 italic capitalize ml-1 truncate">
                                                                    ({m.concepto || 'efectivo'})
                                                                </span>
                                                            )}
                                                            {isNota && m.concepto && (
                                                                <span className="text-xs text-zinc-600 italic ml-1 truncate">
                                                                    {m.concepto}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-1 shrink-0">
                                                            <span className="text-xs sm:text-sm text-zinc-700 whitespace-nowrap">
                                                                {formatFechaDMA(m.fecha)}
                                                            </span>
                                                            {m.tipo === 'abono' && m.comprobante_url && (
                                                                <a href={`${API_URL}${m.comprobante_url}`} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                                                                    <Eye className="w-3.5 h-3.5" />
                                                                </a>
                                                            )}
                                                            {isAdmin && m.tipo === 'abono' && (
                                                                <>
                                                                    <Button size="sm" variant="ghost" onClick={() => handleEditAbono(m._abono)} className="h-6 w-6 p-0 text-blue-700 hover:bg-blue-100" data-testid={`btn-edit-abono-${m.id}`} title="Corregir">
                                                                        <Edit2 className="w-3.5 h-3.5" />
                                                                    </Button>
                                                                    <Button size="sm" variant="ghost" onClick={() => handleDeleteAbono(m._abono)} className="h-6 w-6 p-0 text-red-700 hover:bg-red-100" data-testid={`btn-delete-abono-${m.id}`} title="Eliminar">
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </Button>
                                                                </>
                                                            )}
                                                            {isAdmin && isNota && (m._credito?.num_abonos === 0) && (
                                                                <Button size="sm" variant="ghost" onClick={() => handleDeleteCredito(m._credito)} className="h-6 w-6 p-0 text-red-700 hover:bg-red-100" title="Eliminar nota">
                                                                    <Trash2 className="w-3.5 h-3.5" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="text-right mt-1">
                                                        <span className="text-sm text-zinc-600">Saldo: </span>
                                                        <span className={`font-extrabold text-lg ${m.saldo > 0.01 ? 'text-amber-700' : 'text-emerald-700'} underline decoration-double underline-offset-2`}>
                                                            ${m.saldo.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {detalleTab === 'notas' && (
                                <div data-testid="seccion-notas">
                                    <div className="space-y-2">
                                        {creditosOrdenados.length === 0 && <p className="text-zinc-500 text-sm text-center py-4">Sin notas registradas</p>}
                                        {creditosOrdenados.map((cr, idx) => {
                                            const abonosDeNota = abonosOrdenados.filter(a => a.credito_id === cr.id);
                                            return (
                                            <div key={cr.id} className="p-3 rounded-lg bg-zinc-800/50 border border-zinc-700">
                                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-300 font-bold">Nota {idx + 1}</span>
                                                            <span className="text-sm text-zinc-400">{formatFechaDMA(cr.fecha)}</span>
                                                            {cr.estatus === 'saldada' ? (
                                                                <span className="badge-success">Saldada</span>
                                                            ) : (
                                                                <span className="badge-warning">Pendiente</span>
                                                            )}
                                                            {cr.venta_id && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-400/20 text-blue-300">POS</span>}
                                                        </div>
                                                        <p className="text-sm font-medium text-zinc-100 mt-1 break-words">{cr.concepto}</p>
                                                        {cr.observaciones && <p className="text-xs text-zinc-500 mt-1">{cr.observaciones}</p>}
                                                    </div>
                                                    <div className="flex flex-col items-end gap-1 shrink-0">
                                                        <p className="text-xs text-zinc-500">Monto: <span className="text-zinc-200">${cr.monto.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span></p>
                                                        <p className="text-xs text-emerald-400">Abonado: ${cr.abonado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                                                        <p className="text-sm font-bold text-amber-400">Saldo: ${cr.saldo.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                                                        <div className="flex gap-1 mt-1">
                                                            {cr.saldo > 0.01 && (
                                                                <Button size="sm" onClick={() => openAbono(cr)} className="h-7 bg-emerald-500 hover:bg-emerald-600 text-xs" data-testid={`btn-abono-${cr.id}`}>
                                                                    <DollarSign className="w-3 h-3 mr-1" /> Abonar
                                                                </Button>
                                                            )}
                                                            {isAdmin && cr.num_abonos === 0 && (
                                                                <Button size="sm" variant="ghost" onClick={() => handleDeleteCredito(cr)} className="h-7 text-red-400">
                                                                    <Trash2 className="w-3 h-3" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Lista de abonos aplicados a esta nota */}
                                                {abonosDeNota.length > 0 && (
                                                    <div className="mt-3 pt-3 border-t border-zinc-700/70 space-y-1.5">
                                                        <p className="text-[11px] text-zinc-500 uppercase tracking-wide font-semibold">
                                                            Pagos aplicados ({abonosDeNota.length})
                                                        </p>
                                                        {abonosDeNota.map((ab) => (
                                                            <div key={ab.id} className="flex items-center justify-between gap-2 text-xs bg-zinc-900/50 rounded px-2 py-1.5" data-testid={`abono-detalle-${ab.id}`}>
                                                                <div className="flex items-center gap-2 flex-wrap min-w-0">
                                                                    <span className="text-zinc-400">{formatFechaDMA(ab.fecha)}</span>
                                                                    <span className="text-emerald-400 font-bold">-${(ab.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                                                                    <span className="px-1.5 py-0.5 rounded bg-zinc-700 text-zinc-200 capitalize text-[10px] font-semibold">
                                                                        {ab.forma_pago || 'efectivo'}
                                                                    </span>
                                                                    {ab.observaciones && (
                                                                        <span className="text-zinc-500 italic truncate max-w-[180px]" title={ab.observaciones}>
                                                                            {ab.observaciones}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-1 shrink-0">
                                                                    {ab.comprobante_url ? (
                                                                        <a
                                                                            href={`${API_URL}${ab.comprobante_url}`}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            className="flex items-center gap-1 px-2 py-0.5 rounded bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 text-[10px] font-semibold"
                                                                            data-testid={`btn-ver-comprobante-${ab.id}`}
                                                                            title="Ver comprobante"
                                                                        >
                                                                            <Eye className="w-3 h-3" /> Ver
                                                                        </a>
                                                                    ) : (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleSubirComprobanteAbono(ab)}
                                                                            className="flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 text-[10px] font-semibold"
                                                                            data-testid={`btn-subir-comprobante-${ab.id}`}
                                                                            title="Subir comprobante"
                                                                        >
                                                                            <Upload className="w-3 h-3" /> Subir
                                                                        </button>
                                                                    )}
                                                                    {isAdmin && (
                                                                        <>
                                                                            <Button size="sm" variant="ghost" onClick={() => handleEditAbono(ab)} className="h-6 w-6 p-0 text-blue-400" title="Corregir">
                                                                                <Edit2 className="w-3 h-3" />
                                                                            </Button>
                                                                            <Button size="sm" variant="ghost" onClick={() => handleDeleteAbono(ab)} className="h-6 w-6 p-0 text-red-400" title="Eliminar">
                                                                                <Trash2 className="w-3 h-3" />
                                                                            </Button>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Seccion historial legacy retirada: ahora todo vive en la pestaña Libreta */}

                            {/* Resumen final: totales al fondo de la ficha */}
                            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-zinc-700">
                                <div className="p-3 rounded-lg bg-zinc-800/70 border border-zinc-700">
                                    <p className="text-xs text-zinc-500">Total Facturado</p>
                                    <p className="text-lg font-bold text-zinc-100">${detalle.total_facturado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                                </div>
                                <div className="p-3 rounded-lg bg-zinc-800/70 border border-zinc-700">
                                    <p className="text-xs text-zinc-500">Total Abonado</p>
                                    <p className="text-lg font-bold text-emerald-400">${detalle.total_abonado.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                                </div>
                                <div className="p-3 rounded-lg bg-amber-400/10 border border-amber-400/30">
                                    <p className="text-xs text-amber-400">Saldo Pendiente</p>
                                    <p className="text-lg font-bold text-amber-400">${detalle.saldo_pendiente.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                                </div>
                            </div>
                        </div>
                        );
                    })()}
                </DialogContent>
            </Dialog>

            {/* Modal: Nuevo Credito */}
            <Dialog open={creditoModalOpen} onOpenChange={setCreditoModalOpen}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    <DialogHeader><DialogTitle>Nueva cuenta a credito</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                        <div className="form-group">
                            <Label className="form-label">Fecha</Label>
                            <Input type="date" value={creditoForm.fecha} onChange={(e) => setCreditoForm({ ...creditoForm, fecha: e.target.value })} />
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Concepto *</Label>
                            <Input value={creditoForm.concepto} onChange={(e) => setCreditoForm({ ...creditoForm, concepto: e.target.value })} placeholder="Ej: 10 pantalones modelo X" data-testid="input-credito-concepto" />
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Monto *</Label>
                            <Input type="number" step="0.01" min="0" value={creditoForm.monto} onChange={(e) => setCreditoForm({ ...creditoForm, monto: parseFloat(e.target.value) || 0 })} data-testid="input-credito-monto" />
                        </div>
                        <div className="form-group">
                            <Label className="form-label">Observaciones</Label>
                            <Textarea rows={2} value={creditoForm.observaciones} onChange={(e) => setCreditoForm({ ...creditoForm, observaciones: e.target.value })} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setCreditoModalOpen(false)}>Cancelar</Button>
                        <Button onClick={handleCreditoSubmit} className="bg-amber-400 hover:bg-amber-500 text-zinc-900 font-bold" data-testid="btn-guardar-credito">Registrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal: Nuevo Abono */}
            <Dialog open={abonoModalOpen} onOpenChange={setAbonoModalOpen}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                    <DialogHeader><DialogTitle>Registrar abono</DialogTitle></DialogHeader>
                    {selectedCredito && (
                        <div className="space-y-3 py-2">
                            <div className="p-3 rounded-lg bg-zinc-800/70 border border-zinc-700 text-sm">
                                <p className="text-zinc-400">{selectedCredito.concepto}</p>
                                <p className="text-amber-400 font-bold">Saldo pendiente: ${selectedCredito.saldo.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Fecha</Label>
                                <Input type="date" value={abonoForm.fecha} onChange={(e) => setAbonoForm({ ...abonoForm, fecha: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Monto *</Label>
                                <Input type="number" step="0.01" min="0" max={selectedCredito.saldo} value={abonoForm.monto} onChange={(e) => setAbonoForm({ ...abonoForm, monto: parseFloat(e.target.value) || 0 })} data-testid="input-abono-monto" />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Forma de pago</Label>
                                <Select value={abonoForm.forma_pago} onValueChange={(v) => setAbonoForm({ ...abonoForm, forma_pago: v })}>
                                    <SelectTrigger data-testid="select-abono-forma"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="efectivo">Efectivo</SelectItem>
                                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                                        <SelectItem value="transferencia">Transferencia</SelectItem>
                                        <SelectItem value="otro">Otro</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Observaciones</Label>
                                <Textarea rows={2} value={abonoForm.observaciones} onChange={(e) => setAbonoForm({ ...abonoForm, observaciones: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Comprobante / Foto del recibo (opcional)</Label>
                                <Input type="file" accept="image/*" onChange={(e) => setComprobanteFile(e.target.files?.[0] || null)} data-testid="input-abono-comprobante" />
                                {comprobanteFile && <p className="text-xs text-emerald-400 mt-1">{comprobanteFile.name}</p>}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAbonoModalOpen(false)}>Cancelar</Button>
                        <Button onClick={handleAbonoSubmit} className="bg-emerald-500 hover:bg-emerald-600 font-bold" data-testid="btn-guardar-abono">Registrar abono</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal: Abono al saldo total (FIFO) */}
            <Dialog open={!!abonoGlobalModal} onOpenChange={(o) => { if (!o) { setAbonoGlobalModal(null); setComprobanteGlobalFile(null); } }}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="modal-abono-global">
                    <DialogHeader><DialogTitle className="flex items-center gap-2"><DollarSign className="w-5 h-5 text-emerald-400" /> Abonar al saldo total</DialogTitle></DialogHeader>
                    {abonoGlobalModal && (
                        <div className="space-y-3 py-2">
                            <div className="p-3 rounded-lg bg-zinc-800/70 border border-zinc-700">
                                <p className="text-sm font-medium text-zinc-100">{abonoGlobalModal.nombre}</p>
                                <p className="text-amber-400 font-bold text-lg mt-1">
                                    Saldo total: ${(abonoGlobalModal.saldo_pendiente || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                </p>
                                <p className="text-[10px] text-zinc-500">
                                    El abono se aplicara a las cuentas pendientes mas antiguas primero.
                                    Cuentas pendientes: {abonoGlobalModal.num_creditos_pendientes || 0}
                                </p>
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Fecha</Label>
                                <Input type="date" value={abonoGlobalForm.fecha} onChange={(e) => setAbonoGlobalForm({ ...abonoGlobalForm, fecha: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Monto a abonar *</Label>
                                <Input
                                    type="number" step="0.01" min="0" max={abonoGlobalModal.saldo_pendiente || 0}
                                    value={abonoGlobalForm.monto}
                                    onChange={(e) => setAbonoGlobalForm({ ...abonoGlobalForm, monto: parseFloat(e.target.value) || 0 })}
                                    placeholder="0.00"
                                    data-testid="input-abono-global-monto"
                                />
                                <div className="flex gap-2 mt-2">
                                    <Button type="button" size="sm" variant="outline" className="text-xs h-7"
                                        onClick={() => setAbonoGlobalForm({ ...abonoGlobalForm, monto: abonoGlobalModal.saldo_pendiente || 0 })}>
                                        Pagar todo (${(abonoGlobalModal.saldo_pendiente || 0).toFixed(2)})
                                    </Button>
                                </div>
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Forma de pago</Label>
                                <Select value={abonoGlobalForm.forma_pago} onValueChange={(v) => setAbonoGlobalForm({ ...abonoGlobalForm, forma_pago: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="efectivo">Efectivo</SelectItem>
                                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                                        <SelectItem value="transferencia">Transferencia</SelectItem>
                                        <SelectItem value="otro">Otro</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Observaciones</Label>
                                <Textarea rows={2} value={abonoGlobalForm.observaciones} onChange={(e) => setAbonoGlobalForm({ ...abonoGlobalForm, observaciones: e.target.value })} />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Comprobante (opcional)</Label>
                                <Input type="file" accept="image/*" onChange={(e) => setComprobanteGlobalFile(e.target.files?.[0] || null)} />
                                {comprobanteGlobalFile && <p className="text-xs text-emerald-400 mt-1">{comprobanteGlobalFile.name}</p>}
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAbonoGlobalModal(null)}>Cancelar</Button>
                        <Button onClick={handleAbonoGlobalSubmit} className="bg-emerald-500 hover:bg-emerald-600 font-bold" data-testid="btn-guardar-abono-global">
                            Aplicar abono
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Modal: Editar Abono existente */}
            <Dialog open={!!editAbonoModal} onOpenChange={(o) => !o && setEditAbonoModal(null)}>
                <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="modal-editar-abono">
                    <DialogHeader>
                        <DialogTitle>Corregir abono</DialogTitle>
                    </DialogHeader>
                    {editAbonoModal && (
                        <div className="space-y-3">
                            <p className="text-xs text-zinc-500">
                                Ajusta los datos si el abono se capturo con error. El monto no puede exceder
                                el saldo disponible de la cuenta asociada.
                            </p>
                            <div className="form-group">
                                <Label className="form-label">Fecha</Label>
                                <Input
                                    type="date"
                                    value={editAbonoModal.fecha}
                                    onChange={(e) => setEditAbonoModal({ ...editAbonoModal, fecha: e.target.value })}
                                    data-testid="input-edit-abono-fecha"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Monto</Label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    value={editAbonoModal.monto}
                                    onChange={(e) => setEditAbonoModal({ ...editAbonoModal, monto: e.target.value })}
                                    placeholder="0.00"
                                    data-testid="input-edit-abono-monto"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Forma de pago</Label>
                                <Select
                                    value={editAbonoModal.forma_pago}
                                    onValueChange={(v) => setEditAbonoModal({ ...editAbonoModal, forma_pago: v })}
                                >
                                    <SelectTrigger data-testid="select-edit-abono-forma-pago"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="efectivo">Efectivo</SelectItem>
                                        <SelectItem value="tarjeta">Tarjeta</SelectItem>
                                        <SelectItem value="transferencia">Transferencia</SelectItem>
                                        <SelectItem value="otro">Otro</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Observaciones</Label>
                                <Textarea
                                    rows={2}
                                    value={editAbonoModal.observaciones}
                                    onChange={(e) => setEditAbonoModal({ ...editAbonoModal, observaciones: e.target.value })}
                                    data-testid="input-edit-abono-observaciones"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditAbonoModal(null)}>Cancelar</Button>
                        <Button
                            onClick={handleSaveEditAbono}
                            className="bg-blue-500 hover:bg-blue-600 font-bold"
                            data-testid="btn-guardar-edit-abono"
                        >
                            Guardar cambios
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
